import React, { useState, useEffect } from "react";
import { ref, onValue } from "firebase/database";
import { database } from "../../../firebaseConfig";
import { X, Store, Truck, TrendingUp, Calendar } from "lucide-react";

const ViewShopModal = ({ shop, companyName, onClose }) => {
  const [deliveries, setDeliveries] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!shop || !companyName) return;

    const deliveriesRef = ref(database, `Companies/${companyName}/ShopDeliveries/${shop.id}`);
    const salesRef = ref(database, `Companies/${companyName}/ShopSales/${shop.id}`);

    let deliveriesLoaded = false;
    let salesLoaded = false;

    const checkLoading = () => {
      if (deliveriesLoaded && salesLoaded) {
        setLoading(false);
      }
    };

    const unsubDeliveries = onValue(deliveriesRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const deliveriesList = [];
        Object.entries(data).forEach(([year, months]) => {
          Object.entries(months || {}).forEach(([month, items]) => {
            Object.values(items || {}).forEach((delivery) => {
              deliveriesList.push(delivery);
            });
          });
        });
        deliveriesList.sort((a, b) => new Date(b.deliveryDate) - new Date(a.deliveryDate));
        setDeliveries(deliveriesList.slice(0, 10));
      }
      deliveriesLoaded = true;
      checkLoading();
    });

    const unsubSales = onValue(salesRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const salesList = [];
        Object.entries(data).forEach(([year, months]) => {
          Object.entries(months || {}).forEach(([month, items]) => {
            Object.values(items || {}).forEach((sale) => {
              salesList.push(sale);
            });
          });
        });
        salesList.sort((a, b) => new Date(b.saleDate) - new Date(a.saleDate));
        setSales(salesList.slice(0, 10));
      }
      salesLoaded = true;
      checkLoading();
    });

    return () => {
      unsubDeliveries();
      unsubSales();
    };
  }, [shop, companyName]);

  if (!shop) return null;

  const totalDelivered = deliveries.reduce((sum, d) => sum + (d.quantity || 0), 0);
  const totalSold = sales.reduce((sum, s) => sum + (s.quantity || 0), 0);
  const totalRevenue = sales.reduce((sum, s) => sum + (s.totalAmount || 0), 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-cyan-700 flex items-center gap-2">
            <Store size={24} />
            Shop Details
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Shop Information */}
          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3">Shop Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-500">Shop Name</p>
                <p className="font-medium">{shop.shopName}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Location</p>
                <p className="font-medium">{shop.location || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Manager</p>
                <p className="font-medium">{shop.managerName || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Phone</p>
                <p className="font-medium">{shop.phoneNumber || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="font-medium">{shop.email || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Status</p>
                <span
                  className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                    shop.status === "Active"
                      ? "bg-green-100 text-green-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {shop.status}
                </span>
              </div>
            </div>
          </div>

          {/* Stock & Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Current Stock</p>
                <Store className="text-cyan-500" size={20} />
              </div>
              <p className="text-2xl font-bold text-cyan-600">
                {parseFloat(shop.currentStock || 0).toLocaleString()} L
              </p>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Total Delivered</p>
                <Truck className="text-blue-500" size={20} />
              </div>
              <p className="text-2xl font-bold text-blue-600">
                {totalDelivered.toLocaleString()} L
              </p>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Total Sold</p>
                <TrendingUp className="text-green-500" size={20} />
              </div>
              <p className="text-2xl font-bold text-green-600">
                {totalSold.toLocaleString()} L
              </p>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Total Revenue</p>
                <TrendingUp className="text-purple-500" size={20} />
              </div>
              <p className="text-2xl font-bold text-purple-600">
                KES {totalRevenue.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Recent Deliveries */}
          <div>
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Truck size={20} />
              Recent Deliveries
            </h4>
            <div className="bg-white border rounded-lg overflow-hidden">
              {loading ? (
                <p className="p-4 text-center text-gray-500">Loading...</p>
              ) : deliveries.length === 0 ? (
                <p className="p-4 text-center text-gray-500">No deliveries yet</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3">Date</th>
                      <th className="text-left p-3">Quantity (L)</th>
                      <th className="text-left p-3">Driver</th>
                      <th className="text-left p-3">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveries.map((delivery, idx) => (
                      <tr key={idx} className="border-t hover:bg-gray-50">
                        <td className="p-3">
                          {new Date(delivery.deliveryDate).toLocaleDateString()}
                        </td>
                        <td className="p-3 font-medium">{delivery.quantity}</td>
                        <td className="p-3">{delivery.driverName || "N/A"}</td>
                        <td className="p-3 text-xs text-gray-500">
                          {delivery.deliveryNotes || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Recent Sales */}
          <div>
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <TrendingUp size={20} />
              Recent Sales
            </h4>
            <div className="bg-white border rounded-lg overflow-hidden">
              {loading ? (
                <p className="p-4 text-center text-gray-500">Loading...</p>
              ) : sales.length === 0 ? (
                <p className="p-4 text-center text-gray-500">No sales recorded yet</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3">Date</th>
                      <th className="text-left p-3">Quantity (L)</th>
                      <th className="text-left p-3">Unit Price</th>
                      <th className="text-left p-3">Total Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.map((sale, idx) => (
                      <tr key={idx} className="border-t hover:bg-gray-50">
                        <td className="p-3">
                          {new Date(sale.saleDate).toLocaleDateString()}
                        </td>
                        <td className="p-3 font-medium">{sale.quantity}</td>
                        <td className="p-3">KES {sale.unitPrice}</td>
                        <td className="p-3 font-semibold">
                          KES {sale.totalAmount.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Shop Timeline */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Calendar className="text-gray-600" size={20} />
              <div>
                <p className="text-sm text-gray-600">Created</p>
                <p className="text-sm font-medium text-gray-800">
                  {shop.createdAt ? new Date(shop.createdAt).toLocaleDateString() : "N/A"}
                </p>
              </div>
            </div>
            {shop.lastDeliveryDate && (
              <div className="flex items-center gap-2">
                <Truck className="text-gray-600" size={20} />
                <div>
                  <p className="text-sm text-gray-600">Last Delivery</p>
                  <p className="text-sm font-medium text-gray-800">
                    {new Date(shop.lastDeliveryDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t p-4">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewShopModal;
